const finiteNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const positiveNumber = (...values) => {
  for (const value of values) {
    const parsed = finiteNumber(value);
    if (parsed !== null && parsed > 0) return parsed;
  }
  return null;
};

export const getTrackPlaylistIndex = (track, fallbackIndex = null) => {
  const rawIndex = finiteNumber(track?.rawItemIndex);
  if (rawIndex !== null && Number.isInteger(rawIndex) && rawIndex >= 0) {
    return rawIndex;
  }

  const fallback = finiteNumber(fallbackIndex);
  return fallback !== null && Number.isInteger(fallback) && fallback >= 0
    ? fallback
    : null;
};

export const hasAuthoritativePlayoutTiming = (progress) => {
  const serverTime = finiteNumber(progress?.serverTime);
  const remaining = finiteNumber(progress?.transitionRemainingSec);
  const duration = finiteNumber(progress?.effectiveDurationSec);
  return serverTime !== null
    && serverTime > 0
    && remaining !== null
    && remaining >= 0
    && duration !== null
    && duration >= 0;
};

export const mergeLiveNowPlayingTrack = (playlistTrack, runtimeTrack) => {
  const hasRuntimeIdentity = runtimeTrack
    && (runtimeTrack.title || runtimeTrack.artist);
  if (hasRuntimeIdentity) {
    return { ...(playlistTrack || {}), ...runtimeTrack };
  }
  return playlistTrack || null;
};

export const getPlayoutDuration = (track) => {
  if (!track) return 0;

  const cueIn = finiteNumber(track.cueIn ?? track.cue_in) || 0;
  const transitionAt = positiveNumber(
    track.beatmixOrigStartNext,
    track.startNext,
    track.start_next
  );
  if (transitionAt !== null && transitionAt > cueIn) {
    return transitionAt - cueIn;
  }

  const cueOut = positiveNumber(track.cueOut, track.cue_out);
  if (cueOut !== null && cueOut > cueIn) {
    return cueOut - cueIn;
  }

  const duration = Math.max(0, finiteNumber(track.duration) || 0);
  return duration > cueIn ? duration - cueIn : duration;
};

export const getIntroDuration = (track) => {
  if (!track || track.beatmixIntroSkipApplied) return 0;

  const intro = finiteNumber(track.intro ?? track.introLength);
  if (intro === null || intro <= 0) return 0;

  const cueIn = finiteNumber(track.cueIn ?? track.cue_in) || 0;
  let mixCuePoints = track.mix_cue_points;
  if (typeof mixCuePoints === 'string') {
    try {
      mixCuePoints = JSON.parse(mixCuePoints);
    } catch (_) {
      mixCuePoints = null;
    }
  }
  const mixDone = finiteNumber(mixCuePoints?.mix_done);
  if (track.mixblock_position === 0 && mixDone !== null && mixDone > cueIn) {
    return mixDone - cueIn;
  }

  // Library intro markers are absolute file positions. Mixblock-derived
  // intro lengths are already relative and are handled above.
  return intro > cueIn ? intro - cueIn : intro;
};

export const buildLiveTimingProjection = ({
  currentIndex,
  currentTrack,
  upNext = [],
  startedAt,
  currentRemainingSec = null,
  observedAt = null
}) => {
  const startedAtMs = startedAt instanceof Date
    ? startedAt.getTime()
    : new Date(startedAt).getTime();
  if (!Number.isFinite(startedAtMs) || currentIndex === null || currentIndex === undefined) {
    return new Map();
  }

  const projection = new Map();
  const sequence = [currentTrack, ...(Array.isArray(upNext) ? upNext : [])].filter(Boolean);
  let cursorMs = startedAtMs;
  let fallbackIndex = currentIndex;

  sequence.forEach((track, sequenceIndex) => {
    const itemIndex = sequenceIndex === 0
      ? currentIndex
      : getTrackPlaylistIndex(track, fallbackIndex + 1);
    const duration = getPlayoutDuration(track);
    projection.set(itemIndex, {
      startTimeMs: cursorMs,
      duration,
      track
    });
    if (sequenceIndex === 0
        && currentRemainingSec !== null
        && currentRemainingSec !== undefined
        && Number.isFinite(Number(currentRemainingSec))) {
      const observedAtMs = observedAt == null
        ? NaN
        : (observedAt instanceof Date
          ? observedAt.getTime()
          : new Date(observedAt).getTime());
      cursorMs = (Number.isFinite(observedAtMs) ? observedAtMs : startedAtMs)
        + Math.max(0, Number(currentRemainingSec)) * 1000;
    } else {
      cursorMs += duration * 1000;
    }
    fallbackIndex = itemIndex;
  });

  return projection;
};
